import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';
import type { ComponentPageData } from '@/features/content/useComponent';

import { useAssignPreLecture } from './hooks/useAssignPreLecture';
import styles from './AssignPreLectureDialog.module.css';

interface AssignPreLectureDialogProps {
  component: ComponentPageData;
  componentId: string;
  courseId: string;
  onOpenChange(nextOpen: boolean): void;
  open: boolean;
}

export function AssignPreLectureDialog({
  component,
  componentId,
  courseId,
  onOpenChange,
  open,
}: AssignPreLectureDialogProps) {
  const mutation = useAssignPreLecture();
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedQuestionIds([]);
  }, [component, open]);

  async function handleSave() {
    await mutation.mutateAsync({
      component,
      componentId,
      courseId,
      selectedQuestionIds,
    });

    onOpenChange(false);
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content}>
          <div className={styles.header}>
            <Dialog.Title className={styles.title}>
              Assign Pre-Lecture Questions
            </Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.description}>
                Select the homework questions you want to move into the
                pre-lecture section. Selected questions will be removed from
                homework and assigned to pre-lecture.
              </p>
            </Dialog.Description>
          </div>

          <div className={styles.body}>
            <div className={styles.selectorGrid}>
              {component.controlPanel.homeworkQuestionOptions.map((question) => (
                <label className={styles.questionCard} key={question.id}>
                  <input
                    checked={selectedQuestionIds.includes(question.id)}
                    onChange={(event) => {
                      setSelectedQuestionIds((current) =>
                        event.target.checked
                          ? [...current, question.id]
                          : current.filter((questionId) => questionId !== question.id),
                      );
                    }}
                    type="checkbox"
                  />
                  <span className={styles.questionLabel}>
                    Question {question.index + 1}
                  </span>
                  <span className={styles.questionType}>{question.typeLabel}</span>
                  <span className={styles.questionPreview}>{question.previewText}</span>
                </label>
              ))}
            </div>
          </div>

          {mutation.isError ? (
            <p aria-live="polite" className={styles.error} role="alert">
              {mutation.error.message}
            </p>
          ) : null}

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button className={styles.cancelButton} variant="secondary">
                CANCEL
              </Button>
            </Dialog.Close>
            <Button
              className={styles.saveButton}
              disabled={mutation.isPending || selectedQuestionIds.length === 0}
              onClick={() => {
                void handleSave();
              }}
              variant="primary"
            >
              {mutation.isPending ? 'Assigning…' : 'ASSIGN PRE-LECTURE'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
